import { createContext, useContext, useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import clsx from 'clsx';

/**
 *  const tabs = [
 *  {
 *    label: 'tab 1',
 *    key: 'tab_1',
 * },
 * {
 *    label: 'tab 2',
 *    key: 'tab_2',
 * }
 * ]
 */

const TabsContext = createContext({});
const { Provider } = TabsContext;

const WithProvider = ({ onChange = () => {}, children }) => {
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (currentTab === undefined) return;
    onChange(currentTab);
  }, [currentTab]);

  return <Provider value={{ currentTab, setCurrentTab }}>{children}</Provider>;
};

const TabElement = ({ children, index, className, ...props }) => {
  const { currentTab } = useContext(TabsContext);

  return (
    <div
      className={clsx( className)}
      role="tabpanel"
      hidden={index !== currentTab}
      id={`composed-tabpanel-${index}`}
      aria-labelledby={`composed-tab-${index}`}
      {...props}
    >
      {index === currentTab && <Box sx={{ p: '2.4rem' }}>{children}</Box>}
    </div>
  );
};

/**
 * ComposedTags
 *
 * Componente de tabs personalizables con soporte para paneles y eventos de cambio.
 *
 * @component
 * @param {React.ReactNode} children - Paneles/tab a mostrar (usualmente ComposedTags.Element)
 * @param {function} [onSelectedTab] - Callback al cambiar de tab (índice)
 * @returns {JSX.Element}
 */
export const ComposedTags = ({ children, onSelectedTab = () => {}, ...props }) => {
  return (
    <WithProvider onChange={onSelectedTab}>
      <ContainerTabs {...props}>{children}</ContainerTabs>
    </WithProvider>
  );
};

const ContainerTabs = ({
  tabs = [],
  color,
  children,
  fontSize,
  width,
  height,
  onBeforeChange,
  sx,
  ...props
}) => {
  const { currentTab, setCurrentTab } = useContext(TabsContext);

  const handleChange = async (event, newTab) => {
    if (typeof onBeforeChange !== 'function') {
      setCurrentTab(newTab);
      return;
    }
    try {
      const result = await onBeforeChange(newTab);
      if (!result) return;
      setCurrentTab(newTab);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        ...sx,
      }}
      {...props}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={handleChange}
          aria-label="composed tabs panel"
          textColor={color || 'primary'}
          indicatorColor={color || 'primary'}
        >
          {tabs.map((tab, index) => {
            return (
              <Tab
                className="flex flex-row flex-1"
                wrapped
                key={`composed-tab-${index}`}
                aria-controls={`composed-tabpanel-${tab.key}`}
                id={`composed-tab-${tab.key}`}
                label={tab.label}
                disabled={tab.disabled ? tab.disabled : undefined}
                icon={tab.icon ? tab.icon : undefined}
                iconPosition={tab.iconPosition ? tab.iconPosition : undefined}
                sx={{
                  fontSize: fontSize || '1rem',
                  width: width || '6rem',
                  height: height || '6rem',
                }}
              />
            );
          })}
        </Tabs>
      </Box>
      {children}
    </Box>
  );
};

/**
 * Element
 *
 * Subcomponente para mostrar el contenido de cada tab.
 *
 * @component
 * @param {React.ReactNode} children - Contenido del tab
 * @param {number} index - Índice del tab
 * @returns {JSX.Element}
 */
ComposedTags.Element = TabElement;
